import { createRouter, createWebHistory } from 'vue-router';
import { whenAuthSettled, readAuthSnapshot } from '@/lib/auth-store';

const AUTH_INIT_TIMEOUT_MS = 5000;

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/Login.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/',
      component: () => import('@/layouts/DashboardLayout.vue'),
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          name: 'dashboard',
          component: () => import('@/views/Dashboard.vue'),
          meta: { title: 'Dashboard' },
        },
        {
          path: 'users',
          name: 'users',
          component: () => import('@/views/Users.vue'),
          meta: { title: 'Users' },
        },
        {
          path: 'users/:id',
          name: 'user-detail',
          component: () => import('@/views/UserDetail.vue'),
          meta: { title: 'User Detail' },
        },
        {
          path: 'bug-reports',
          name: 'bug-reports',
          component: () => import('@/views/BugReports.vue'),
          meta: { title: 'Support Reports' },
        },
        {
          path: 'deletion-feedback',
          name: 'deletion-feedback',
          component: () => import('@/views/DeletionFeedback.vue'),
          meta: { title: 'Deletion Feedback' },
        },
        {
          path: 'snaptrade-logs',
          name: 'snaptrade-logs',
          component: () => import('@/views/SnaptradeLogs.vue'),
          meta: { title: 'SnapTrade Logs' },
        },
        {
          path: 'entitlements',
          name: 'entitlements',
          component: () => import('@/views/Entitlements.vue'),
          meta: { title: 'Entitlements' },
        },
        // The page lived at /billing before it was renamed — keep old links working.
        { path: 'billing', redirect: { name: 'entitlements' } },
        {
          path: 'subscriptions',
          name: 'subscriptions',
          component: () => import('@/views/Subscriptions.vue'),
          meta: { title: 'Subscriptions' },
        },
        {
          path: 'catalog',
          name: 'catalog',
          component: () => import('@/views/Catalog.vue'),
          meta: { title: 'Plans & Pricing' },
        },
        {
          path: 'campaigns',
          name: 'campaigns',
          component: () => import('@/views/Campaigns.vue'),
          meta: { title: 'Campaigns' },
        },
        {
          path: 'campaigns/:id',
          name: 'campaign-detail',
          component: () => import('@/views/CampaignDetail.vue'),
          meta: { title: 'Campaign Detail' },
        },
        {
          path: 'broadcasts',
          name: 'broadcasts',
          component: () => import('@/views/Broadcasts.vue'),
          meta: { title: 'Broadcasts' },
        },
        {
          path: 'broadcasts/settings',
          name: 'broadcast-settings',
          component: () => import('@/views/BroadcastSettings.vue'),
          meta: { title: 'Broadcast settings' },
        },
        {
          path: 'broadcasts/templates/:templateId',
          name: 'broadcast-template',
          component: () => import('@/views/BroadcastTemplateEditor.vue'),
          meta: { title: 'Email template' },
        },
        {
          path: 'broadcasts/:id',
          name: 'broadcast-detail',
          component: () => import('@/views/BroadcastDetail.vue'),
          meta: { title: 'Broadcast' },
        },
        {
          path: 'company-profiles',
          name: 'company-profiles',
          component: () => import('@/views/CompanyProfiles.vue'),
          meta: { title: 'Company Profiler' },
        },
        {
          path: 'company-profiles/:id',
          name: 'company-profile-detail',
          component: () => import('@/views/CompanyProfileDetail.vue'),
          meta: { title: 'Company Profile' },
        },
        {
          path: 'bulk-sync',
          name: 'bulk-sync',
          component: () => import('@/views/BulkSync.vue'),
          meta: { title: 'Bulk Sync' },
        },
        {
          path: 'backfill',
          name: 'backfill',
          component: () => import('@/views/BackfillView.vue'),
          meta: { title: 'Backfill' },
        },
        {
          path: 'unknown-labels',
          name: 'unknown-labels',
          component: () => import('@/views/UnknownLabels.vue'),
          meta: { title: 'Unknown Labels' },
        },
        {
          path: 'search-engines',
          name: 'search-engines',
          component: () => import('@/views/SearchEngines.vue'),
          meta: { title: 'Web Search' },
        },
      ],
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: '/',
    },
  ],
});

router.beforeEach(async (to, _from, next) => {
  await whenAuthSettled(AUTH_INIT_TIMEOUT_MS);

  // Re-read the snapshot AFTER the wait: the timeout can fire while boot is
  // still retrying, and the decision must reflect the state at that moment.
  const { isAuthenticated, session, isBootBlocked } = readAuthSnapshot();
  const requiresAuth = to.meta.requiresAuth !== false;

  if (requiresAuth && !isAuthenticated) {
    // A session whose backend sync has not settled (slow boot, retry ladder,
    // failed-boot panel) is NOT a logged-out user: proceed and let the App.vue
    // boot gate own the screen instead of bouncing to /login.
    if (session && isBootBlocked) {
      next();
    } else {
      next({ name: 'login' });
    }
  } else if (to.name === 'login' && isAuthenticated) {
    next({ name: 'dashboard' });
  } else {
    next();
  }
});

export default router;
